import type { Socket } from "socket.io-client";
import { connectToWebSocket, GATEWAY_TYPES, getSocket } from "./websocket";

/** How often to look for a socket object that replaced the one we hold. */
const DEFAULT_POLL_MS = 5000;

interface WatchOptions {
	connect?: () => Promise<Socket | null>;
	current?: () => Socket | null;
	pollMs?: number;
}

/**
 * Calls `onConnected(socket, isReconnect)` once per distinct connection to the
 * /chat namespace: once for the first connection, then again every time the
 * connection comes back with a new socket id.
 *
 * Why not `onSocketEvent("connect")`: SocketManager re-attaches registered
 * handlers from INSIDE its own connect handler, so a handler registered that
 * way never sees the connect that attached it, and it can swap in a new
 * socket object on reconnect. This watches the socket object directly and
 * polls for a replacement, keyed on `socket.id`, so a room join or a refetch
 * happens exactly once per connection.
 */
export function watchChatSocket(
	onConnected: (socket: Socket, isReconnect: boolean) => void,
	{
		connect = () => connectToWebSocket(GATEWAY_TYPES.CHAT),
		current = () => getSocket(GATEWAY_TYPES.CHAT),
		pollMs = DEFAULT_POLL_MS,
	}: WatchOptions = {},
): () => void {
	let isStopped = false;
	let lastSocketId: string | null = null;
	const watched: Socket[] = [];

	const check = (candidate?: Socket | null) => {
		if (isStopped) return;
		const socket = candidate ?? current();
		if (!socket?.connected || !socket.id) return;
		watch(socket);
		if (socket.id === lastSocketId) return;
		const isReconnect = lastSocketId !== null;
		lastSocketId = socket.id;
		onConnected(socket, isReconnect);
	};

	const onSocketConnect = () => check();

	const watch = (socket: Socket) => {
		if (watched.includes(socket)) return;
		watched.push(socket);
		socket.on("connect", onSocketConnect);
	};

	connect()
		.then((socket) => check(socket))
		.catch((error) =>
			console.warn("[chatSocketWatch] Could not connect to /chat:", error),
		);
	const timer = setInterval(() => check(), pollMs);

	return () => {
		isStopped = true;
		clearInterval(timer);
		for (const socket of watched) socket.off("connect", onSocketConnect);
	};
}
